import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { theme } from '@bumble/app/theme';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import { Loading } from '@bumble/app/components/Loading';
import NoData from '@bumble/app/components/NoData';
import { RefreshButton } from '@bumble/app/components/RefreshButton';
import { TabGrid, TabGridStructure } from '@bumble/app/components/TabGrid';

const accordionStyles = {
  boxShadow: 'none',
  '&::before': {
    display: 'none',
  },
};

const accordionSummaryStyles = {
  flexDirection: 'row-reverse',
  fontWeight: 700,
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)',
    color: theme.palette.primary.main,
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)',
  },
};

const dhcpColumns: GridColDef[] = [
    {
    field: 'hostname',
    headerName: 'Hostname',
    flex: 1,
    minWidth: 180,
    disableColumnMenu: true,
  },
  {
    field: 'ip_address',
    headerName: 'IP Address',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'mac_address',
    headerName: 'MAC Address',
    width: 180,
    disableColumnMenu: true,
  },
  {
    field: 'lan_interface',
    headerName: 'Interface',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'vendor',
    headerName: 'Vendor',
    width: 180,
    disableColumnMenu: true,
  },
  {
    field: 'expires_in',
    headerName: 'Lease Expiration (s)',
    width: 180,
    disableColumnMenu: true,
  },
];

type NetworkInterfaceStat = Record<string, string | number | null | undefined>;

const NETWORK_STAT_FIELDS: { key: string; label: string }[] = [
  { key: 'Type', label: 'Type' },
  { key: 'IP Address', label: 'IP Addr CIDR' },
  { key: 'Default Gateway', label: 'Default GW Addr' },
  { key: 'DNS Servers', label: 'DNS Servers' },
  { key: 'Admin State', label: 'Admin State' },
  { key: 'Operational State', label: 'Operational State' },
  { key: 'MTU', label: 'MTU' },
  { key: 'MAC Address', label: 'MAC Addr' },
  { key: 'Link Detected', label: 'Link Detected' },
  { key: 'Speed', label: 'Speed' },
  { key: 'Duplex', label: 'Duplex' },
  { key: 'Carrier Up Count', label: 'Carrier Up Count' },
  { key: 'Carrier Down Count', label: 'Carrier Down Count' },
  { key: 'Rx Packets', label: 'Rx Packets' },
  { key: 'Rx Bytes', label: 'Rx Bytes' },
  { key: 'Rx Errors', label: 'Rx Errors' },
  { key: 'Rx Dropped', label: 'Rx Dropped' },
  { key: 'Rx Missed', label: 'Rx Missed' },
  { key: 'Rx Multicast', label: 'Rx Multicast' },
  { key: 'Tx Packets', label: 'Tx Packets' },
  { key: 'Tx Bytes', label: 'Tx Bytes' },
  { key: 'Tx Errors', label: 'Tx Errors' },
  { key: 'Tx Dropped', label: 'Tx Dropped' },
  { key: 'Tx Collisions', label: 'Tx Collisions' },
  { key: 'Tx Carrier', label: 'Tx Carrier' },
];

const CELLULAR_STAT_FIELDS: { key: string; label: string }[] = [
  { key: 'Status', label: 'Status' },
  { key: 'APN', label: 'APN' },
  { key: 'Operator', label: 'Operator' },
  { key: 'Radio Access Technology', label: 'Radio Access Technology' },
  { key: 'Signal Quality', label: 'Signal Quality' },
  { key: 'IMEI', label: 'IMEI' },
  { key: 'ICCID', label: 'ICCID' },
  { key: 'Modem Model', label: 'Modem Model' },
  { key: 'Signal Tech', label: 'Signal Tech' },
  { key: 'RSSI', label: 'RSSI' },
  { key: 'RSRP', label: 'RSRP' },
  { key: 'RSRQ', label: 'RSRQ' },
  { key: 'SNR', label: 'SNR' },
];

const formatNetworkStatValue = (key: string, value: string | number | null | undefined) => {
  if (value == null || value === '') {
    return 'N/A';
  }
  if (key === 'Duplex' && typeof value === 'string') {
    return value.toUpperCase() === 'FULL' ? 'Full' : value;
  }
  if ((key === 'Admin State' || key === 'Operational State') && typeof value === 'string') {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
  return String(value);
};

const buildNetworkStatRows = (iface: NetworkInterfaceStat): TabGridStructure[] => {
  const fields = iface.Type === 'CELLULAR'
    ? [...NETWORK_STAT_FIELDS, ...CELLULAR_STAT_FIELDS]
    : NETWORK_STAT_FIELDS;

  return fields
    .filter(({ key }) => key in iface)
    .map(({ key, label }) => ({
      label,
      content: formatNetworkStatValue(key, iface[key]),
    }));
};

interface RouterTabLanProps {
  cpeId: string;
}

export const RouterTabLan = ({ cpeId }: RouterTabLanProps) => {
  const api = useApi();

  const {
    isLoading: dhcpLoading,
    isRefetching: dhcpRefetching,
    data: dhcpData,
    refetch: refetchDhcpClients,
  } = useQuery(
    ['dhcpClients', cpeId],
    () => api.getDhcpClientsApiV1GetDhcpClientsGet({ node_id: cpeId }),
    {
      enabled: !!cpeId,
      onError: apiError,
    },
  );

  const {
    isLoading: networkStatsLoading,
    isRefetching: networkStatsRefetching,
    data: networkStatsData,
    refetch: refetchNetworkStats,
  } = useQuery(
    ['networkStats', cpeId],
    () => api.getNetworkStatsApiV1GetNetworkStatsGet({ node_id: cpeId }),
    {
      enabled: !!cpeId,
      onError: apiError,
    },
  );

  const isRefreshing = dhcpRefetching || networkStatsRefetching;

  const handleRefresh = () => {
    refetchDhcpClients();
    refetchNetworkStats();
    toast.success('Refreshed LAN data.');
  };

  const dhcpRows =
    dhcpData?.data.map((client) => ({
      ...client,
      id: client.mac_address,
      hostname: client.hostname ?? '',
      lan_interface: (client as { lan_interface?: string }).lan_interface ?? '',
      vendor: (client as { vendor?: string }).vendor ?? '',
    })) ?? [];

  const networkInterfaces = (networkStatsData?.data ?? []).filter(
    (iface): iface is NetworkInterfaceStat => !!iface && typeof iface === 'object',
  );

  return (
    <Box>
      <Accordion defaultExpanded sx={accordionStyles}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles}>
          <Box display="flex" alignItems="center" gap={0.5}>
            CPE-Managed DHCP Clients
            <Box onClick={(e) => e.stopPropagation()}>
              <RefreshButton
                onRefresh={handleRefresh}
                disable={isRefreshing || dhcpLoading || networkStatsLoading}
              />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {dhcpLoading ? (
            <Loading height={120} />
          ) : dhcpRows.length === 0 ? (
            <NoData />
          ) : (
            <DataTableGrid rows={dhcpRows} columns={dhcpColumns} hideSearchBar />
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded sx={{ ...accordionStyles, mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles}>
          Network Stats
        </AccordionSummary>
        <AccordionDetails>
          {networkStatsLoading ? (
            <Loading height={120} />
          ) : networkInterfaces.length === 0 ? (
            <NoData />
          ) : (
            <Box>
              {networkInterfaces.map((iface) => {
                const interfaceName = String(iface.Interface ?? 'Unknown');
                return (
                  <Accordion key={interfaceName} sx={accordionStyles}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles}>
                      Interface: {interfaceName}
                    </AccordionSummary>
                    <AccordionDetails>
                      <TabGrid data={buildNetworkStatRows(iface)} colWidth={3} />
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
